import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Radar, Loader2, MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TwoFactorEnroll } from "@/components/TwoFactor";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { persistSession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [fatal, setFatal] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [enroll, setEnroll] = useState(null);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setFatal(formatApiError(err.response?.data?.detail) || err.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, { password, name });
      if (data.enrollment_required) setEnroll(data.mfa_token);
      else { persistSession(data); navigate("/"); }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Radar className="h-5 w-5" />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Beacon</span>
        </div>

        {fatal ? (
          <div data-testid="invite-invalid">
            <h1 className="font-display text-2xl font-semibold tracking-tight">This link won't work</h1>
            <p className="mt-2 text-sm text-muted-foreground">{fatal}</p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/login")}>Back to sign in</Button>
          </div>
        ) : !invite ? (
          <p className="text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Checking your invite…</p>
        ) : enroll ? (
          <TwoFactorEnroll mfaToken={enroll} onDone={(data) => { persistSession(data); navigate("/"); }} />
        ) : (
          <>
            <div className="mb-2 flex items-center gap-2 text-primary">
              <MailCheck className="h-4 w-4" />
              <p className="text-xs font-semibold uppercase tracking-widest">Invited as {invite.role}</p>
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Set your password</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You're creating the account for <span className="font-medium text-foreground">{invite.email}</span>.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input id="name" data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw">Password</Label>
                <Input id="pw" data-testid="invite-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
                <p className="text-xs text-muted-foreground">At least 12 characters, and not a common password.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw2">Confirm password</Label>
                <Input id="pw2" data-testid="invite-confirm" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="invite-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="invite-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create my account
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
